import {
  FC,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react'
import styled, { keyframes } from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import {
  ChevronUpIcon,
  MicIcon,
  SearchIcon,
  SparklesIcon,
  XIcon
} from './aiSearchIcons'

const MAX_QUERY_LENGTH = 500

const EXPAND_TRANSITION = '0.3s ease'

const EXPANDED_TEXTAREA_HEIGHT = 78

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

interface AiSearchBoxProps {
  textareaRef: RefObject<HTMLTextAreaElement>
  value: string
  isExpanded: boolean
  isListening: boolean
  isDictationSupported: boolean
  onChange: (value: string) => void
  onExpand: () => void
  onCollapse: () => void
  onClear: () => void
  onDictate: () => void
  onSubmit: () => void
  isCompact?: boolean
  isSearching?: boolean
}

const AiSearchBox: FC<AiSearchBoxProps> = ({
  textareaRef,
  value,
  isExpanded,
  isListening,
  isDictationSupported,
  onChange,
  onExpand,
  onCollapse,
  onClear,
  onDictate,
  onSubmit,
  isCompact,
  isSearching
}) => {
  const { t } = useTranslation('common')
  const actionsCount = (value ? 1 : 0) + (isDictationSupported ? 1 : 0)
  const isSubmitInactive = !value.trim()

  const wasExpanded = useRef(isExpanded)

  useIsomorphicLayoutEffect(() => {
    const textarea = textareaRef.current

    if (!textarea) {
      return
    }

    if (isExpanded && wasExpanded.current) {
      textarea.style.height = 'auto'
    }

    textarea.style.height = isExpanded
      ? `${Math.max(EXPANDED_TEXTAREA_HEIGHT, textarea.scrollHeight)}px`
      : '24px'
    wasExpanded.current = isExpanded
  }, [isExpanded, value])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      onSubmit()
    }

    if (event.key === 'Escape' && !isSearching) {
      onCollapse()
    }
  }

  const handleFooterClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target as Element).closest('button')) {
      onCollapse()
    }
  }

  return (
    <StyledBox
      data-expanded={isExpanded}
      data-compact={isCompact}
      data-searching={isSearching}
    >
      <StyledHalo />
      <StyledRim />
      <StyledForm
        onSubmit={handleSubmit}
        data-expanded={isExpanded}
        data-actions={actionsCount}
        aria-busy={isSearching}
      >
        <StyledLeading aria-hidden="true" data-hidden={isExpanded}>
          <SearchIcon />
          <SparklesIcon className="sparkle" />
        </StyledLeading>
        <StyledTextarea
          ref={textareaRef}
          rows={1}
          maxLength={MAX_QUERY_LENGTH}
          value={value}
          readOnly={isListening || isSearching}
          data-expanded={isExpanded}
          placeholder={
            isExpanded
              ? t('Toolbar.AiSearch.inputPlaceholderExpanded')
              : t('Toolbar.AiSearch.inputPlaceholder')
          }
          aria-label={t('Toolbar.AiSearch.inputPlaceholder')}
          enterKeyHint="search"
          autoComplete="off"
          onFocus={onExpand}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <StyledTopActions>
          {value && (
            <StyledIconButton
              type="button"
              aria-label={t('Toolbar.AiSearch.clear')}
              onClick={onClear}
            >
              <XIcon />
            </StyledIconButton>
          )}
          {isDictationSupported && (
            <StyledIconButton
              type="button"
              data-listening={isListening}
              aria-pressed={isListening}
              aria-label={t('Toolbar.AiSearch.dictate')}
              disabled={isSearching}
              onClick={onDictate}
            >
              <MicIcon />
            </StyledIconButton>
          )}
        </StyledTopActions>
        <StyledFooterReveal data-expanded={isExpanded}>
          <div>
            <StyledFooter onClick={handleFooterClick}>
              <StyledIconButton
                type="button"
                aria-label={t('Toolbar.AiSearch.collapse')}
                aria-expanded={isExpanded}
                disabled={isSearching}
                onClick={onCollapse}
              >
                <ChevronUpIcon />
              </StyledIconButton>
              <StyledStatus role="status" aria-live="polite">
                {(isListening || isSearching) && (
                  <StyledWave aria-hidden="true">
                    <b />
                    <b />
                    <b />
                  </StyledWave>
                )}
                <span>
                  {isSearching
                    ? t('Toolbar.AiSearch.statusSearching', null, {
                        default: 'Searching…'
                      })
                    : isListening
                    ? t('Toolbar.AiSearch.statusListening')
                    : t('Toolbar.AiSearch.statusIdle')}
                </span>
              </StyledStatus>
              <StyledSubmit
                type={isSubmitInactive ? 'button' : 'submit'}
                disabled={isListening || isSearching}
                aria-disabled={isSubmitInactive}
                data-searching={isSearching}
                onClick={isSubmitInactive ? onCollapse : undefined}
              >
                {t('Toolbar.AiSearch.submit')}
                <SearchIcon />
              </StyledSubmit>
            </StyledFooter>
          </div>
        </StyledFooterReveal>
      </StyledForm>
    </StyledBox>
  )
}

export default AiSearchBox

const wave = keyframes`
  0%, 70%, 100% {
    transform: scaleY(0.35);
    opacity: 0.55;
  }

  35% {
    transform: scaleY(1);
    opacity: 1;
  }
`

const flow = keyframes`
  from {
    background-position: 0% 50%;
  }

  to {
    background-position: 150% 50%;
  }
`

const breathe = keyframes`
  0%, 100% {
    opacity: 0.4;
  }

  50% {
    opacity: 0.8;
  }
`

const StyledBox = styled.div`
  position: relative;
  isolation: isolate;
  box-sizing: border-box;
  padding: 1.5px;
  border-radius: 27px;
  color: #242428;
  transition: border-radius ${EXPAND_TRANSITION};

  &[data-expanded='true'] {
    border-radius: 22px;
  }

  &[data-compact='true'][data-expanded='false'] {
    border-radius: 20px;
  }

  svg {
    display: block;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    * {
      animation: none !important;
      transition: none !important;
    }
  }
`

const StyledRim = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    #37cbbd 0%,
    #37cbbd 12%,
    #e875b7 48%,
    #e875b7 52%,
    #c5df6b 88%,
    #c5df6b 100%
  );
  background-size: 115% 100%;
  background-position: 50% 50%;

  [data-searching='true'] > & {
    background: linear-gradient(
      90deg,
      #37cbbd 0%,
      #e875b7 16.6667%,
      #c5df6b 33.3333%,
      #37cbbd 50%,
      #e875b7 66.6667%,
      #c5df6b 83.3333%,
      #37cbbd 100%
    );
    background-size: 300% 100%;
    animation: ${flow} 3s linear infinite;
  }
`

const StyledHalo = styled(StyledRim)`
  filter: blur(7px);
  opacity: 0.3;

  ${StyledBox}:focus-within & {
    opacity: 0.6;
  }

  [data-searching='true'] > & {
    opacity: 0.6;
    animation: ${flow} 3s linear infinite, ${breathe} 2s ease-in-out infinite;
  }
`

const StyledForm = styled.form`
  position: relative;
  box-sizing: border-box;
  margin: 0;
  min-height: 52px;
  padding: 14px 54px 14px 70px;
  border-radius: 25.5px;
  background: #fff;
  transition: padding ${EXPAND_TRANSITION}, border-radius ${EXPAND_TRANSITION};

  &[data-actions='2'] {
    padding-right: 98px;
  }

  &[data-expanded='true'] {
    padding: 13px 14px 12px 18px;
    border-radius: 20.5px;
  }

  [data-compact='true'][data-expanded='false'] & {
    min-height: 37px;
    padding-top: 6.5px;
    padding-bottom: 6.5px;
    border-radius: 18.5px;
  }
`

const StyledLeading = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 9px;
  height: 20px;
  color: #64646f;
  transition: opacity 0.15s ease;

  &[data-hidden='true'] {
    opacity: 0;
    pointer-events: none;
  }

  .sparkle {
    width: 14px;
    height: 14px;
    color: #c45a9b;
  }

  [data-compact='true'][data-expanded='false'] & {
    top: 8.5px;
  }
`

const StyledTextarea = styled.textarea`
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 24px;
  min-height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #242428;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  resize: none;
  transition: height ${EXPAND_TRANSITION};
  outline: none;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: #777780;
    opacity: 1;
  }

  &[data-expanded='true'] {
    height: ${EXPANDED_TEXTAREA_HEIGHT}px;
    min-height: ${EXPANDED_TEXTAREA_HEIGHT}px;
    padding-right: 70px;
    padding-bottom: 8px;
    white-space: pre-wrap;

    &::placeholder {
      color: #909098;
    }
  }
`

const StyledTopActions = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  transition: top ${EXPAND_TRANSITION};

  [data-compact='true'][data-expanded='false'] & {
    top: -3.5px;
  }
`

const StyledIconButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #686873;
  cursor: pointer;

  &[data-listening='true'] {
    color: #b04c87;
  }

  &:disabled {
    cursor: default;
  }
`

const StyledFooterReveal = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  opacity: 0;
  transition: grid-template-rows ${EXPAND_TRANSITION},
    opacity ${EXPAND_TRANSITION}, visibility 0s linear 0.3s;

  > * {
    overflow: hidden;
    min-height: 0;
    margin-left: -8px;
  }

  &[data-expanded='true'] {
    grid-template-rows: 1fr;
    visibility: visible;
    opacity: 1;
    transition: grid-template-rows ${EXPAND_TRANSITION},
      opacity ${EXPAND_TRANSITION}, visibility 0s;
  }
`

const StyledFooter = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  column-gap: 12px;
  min-height: 44px;
  margin: 8px 0 0;

  @media (max-width: 360px) {
    gap: 4px;
  }
`

const StyledStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #73737d;
  font-size: 12px;
  line-height: 16px;
`

const StyledWave = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 3px;
  height: 18px;

  b {
    width: 3px;
    height: 12px;
    border-radius: 3px;
    background: #37b9ac;
    animation: ${wave} 1s ease-in-out infinite;
  }

  b:nth-child(2) {
    background: #d566a5;
    animation-delay: 0.15s;
  }

  b:nth-child(3) {
    background: #a5c345;
    animation-delay: 0.3s;
  }
`

const StyledSubmit = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 13px;
  border: 0;
  border-radius: 13px;
  background: #252529;
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.55;
  }

  &:disabled {
    cursor: default;
  }

  &[data-searching='true']:disabled {
    background: #eeeef2;
    color: #72727c;
    opacity: 1;
  }

  @media (max-width: 360px) {
    padding: 0 10px;
  }
`
